package com.gloopgo.game.ui

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawing
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Scaffold
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardCapitalization
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.gloopgo.game.engine.ANIMAL_SKIN_OPTIONS
import com.gloopgo.game.engine.AnimalSkinOption
import com.gloopgo.game.engine.COLOR_OPTIONS
import com.gloopgo.game.engine.ColorOption
import com.gloopgo.game.engine.GameConstants
import com.gloopgo.game.viewmodel.MenuViewModel
import gloopgo.shared.generated.resources.Res
import gloopgo.shared.generated.resources.menu_default_player_name
import gloopgo.shared.generated.resources.menu_start_single_player
import gloopgo.shared.generated.resources.onboarding_back
import gloopgo.shared.generated.resources.onboarding_cancel
import gloopgo.shared.generated.resources.onboarding_char_range_hint
import gloopgo.shared.generated.resources.onboarding_error_max_chars
import gloopgo.shared.generated.resources.onboarding_error_min_chars
import gloopgo.shared.generated.resources.onboarding_live_preview_hint
import gloopgo.shared.generated.resources.onboarding_next
import gloopgo.shared.generated.resources.onboarding_nickname_label
import gloopgo.shared.generated.resources.onboarding_nickname_placeholder
import gloopgo.shared.generated.resources.onboarding_playing_as
import gloopgo.shared.generated.resources.onboarding_random_button
import gloopgo.shared.generated.resources.onboarding_start_playing
import gloopgo.shared.generated.resources.onboarding_step_1_heading
import gloopgo.shared.generated.resources.onboarding_step_1_subheading
import gloopgo.shared.generated.resources.onboarding_step_2_heading
import gloopgo.shared.generated.resources.onboarding_tab_color
import gloopgo.shared.generated.resources.onboarding_tab_emoji
import gloopgo.shared.generated.resources.nickname_not_allowed
import org.jetbrains.compose.resources.stringResource

enum class OnboardingStep {
    NICKNAME,
    AVATAR
}

/**
 * Full-screen Nav3 onboarding for nickname + avatar/color selection.
 * Adaptive two-pane on expanded / landscape; compact scrollable column otherwise.
 * Used by both Single Player and Multiplayer flows.
 */
@Composable
fun AvatarColorScreen(
    isSinglePlayer: Boolean = false,
    startAtAvatar: Boolean = false,
    onStartGame: (String, String, Long?) -> Unit,
    onBack: () -> Unit,
    viewModel: MenuViewModel
) {
    val uiState by viewModel.uiState.collectAsStateWithLifecycle()
    val initialStep = if (startAtAvatar) {
        val trimmed = uiState.playerName.trim()
        if (trimmed.length >= GameConstants.MIN_PLAYER_NAME_LENGTH && uiState.isValid) {
            OnboardingStep.AVATAR
        } else {
            OnboardingStep.NICKNAME
        }
    } else {
        OnboardingStep.NICKNAME
    }
    var currentStep by rememberSaveable { mutableStateOf(initialStep) }

    val initialEmoji = uiState.selectedEmoji.ifEmpty { ANIMAL_SKIN_OPTIONS.first().emoji }
    var chosenEmoji by rememberSaveable { mutableStateOf(initialEmoji) }
    var chosenColor by rememberSaveable {
        mutableStateOf(
            uiState.selectedColor
                ?: (ANIMAL_SKIN_OPTIONS.find { it.emoji == initialEmoji }?.color
                    ?: ANIMAL_SKIN_OPTIONS.first().color)
        )
    }
    var isColorCustomized by rememberSaveable { mutableStateOf(uiState.selectedColor != null) }
    var activeTab by rememberSaveable { mutableStateOf(AvatarPickerTab.EMOJI) }

    val activeColor = Color(chosenColor)
    val trimmedName = uiState.playerName.trim()
    val isNameValid = uiState.isValid
    val isTooShort = uiState.playerName.isNotEmpty() &&
        trimmedName.length < GameConstants.MIN_PLAYER_NAME_LENGTH
    val hasProfanity = uiState.hasProfanity

    val appBarTitle = stringResource(
        if (currentStep == OnboardingStep.NICKNAME) {
            Res.string.onboarding_step_1_heading
        } else {
            Res.string.onboarding_step_2_heading
        }
    )

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(
                Brush.verticalGradient(
                    listOf(
                        Color(0xFF0F172A),
                        Color(0xFF090D16)
                    )
                )
            )
    ) {
        Scaffold(
            containerColor = Color.Transparent,
            contentWindowInsets = WindowInsets.safeDrawing,
            topBar = {
                NeonAbyssTopAppBar(
                    title = appBarTitle,
                    onBack = {
                        if (currentStep == OnboardingStep.AVATAR) {
                            currentStep = OnboardingStep.NICKNAME
                        } else {
                            onBack()
                        }
                    },
                )
            },
        ) { innerPadding ->
        BoxWithConstraints(
            modifier = Modifier
                .fillMaxSize()
                .padding(innerPadding)
                .padding(horizontal = 16.dp, vertical = 12.dp),
            contentAlignment = Alignment.Center
        ) {
            val isTwoPane = maxWidth > 720.dp || (maxWidth >= 560.dp && maxHeight < 560.dp)
            val isCompactHeight = maxHeight < 560.dp

            Card(
                colors = CardDefaults.cardColors(containerColor = Color(0xFF1E293B)),
                shape = RoundedCornerShape(if (isCompactHeight) 18.dp else 24.dp),
                modifier = Modifier
                    .widthIn(max = if (isTwoPane) 860.dp else 520.dp)
                    .fillMaxWidth(if (isTwoPane) 0.94f else 0.92f)
                    .heightIn(
                        min = if (isTwoPane && !isCompactHeight) 480.dp else 0.dp,
                        max = if (isCompactHeight) (maxHeight - 48.dp).coerceAtLeast(320.dp) else 650.dp
                    )
                    .border(1.5.dp, Color(0xFF334155), RoundedCornerShape(if (isCompactHeight) 18.dp else 24.dp)),
                elevation = CardDefaults.cardElevation(defaultElevation = 8.dp)
            ) {
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .then(
                            if (currentStep == OnboardingStep.AVATAR || isTwoPane) {
                                Modifier.fillMaxHeight()
                            } else {
                                Modifier
                            }
                        )
                        .padding(if (isCompactHeight) 12.dp else 20.dp),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    if (currentStep == OnboardingStep.NICKNAME) {
                        StepNicknameContent(
                            playerName = uiState.playerName,
                            trimmedName = trimmedName,
                            isNameValid = isNameValid,
                            isTooShort = isTooShort,
                            hasProfanity = hasProfanity,
                            activeColor = activeColor,
                            chosenEmoji = chosenEmoji,
                            isTwoPane = isTwoPane,
                            isCompactHeight = isCompactHeight,
                            onNameChange = { viewModel.onPlayerNameChange(it) },
                            onRandomName = { viewModel.generateRandomName() },
                            onNext = { currentStep = OnboardingStep.AVATAR },
                            onCancel = onBack
                        )
                    } else {
                        StepAvatarContent(
                            trimmedName = trimmedName,
                            chosenEmoji = chosenEmoji,
                            chosenColor = chosenColor,
                            activeColor = activeColor,
                            activeTab = activeTab,
                            isSinglePlayer = isSinglePlayer,
                            isTwoPane = isTwoPane,
                            isCompactHeight = isCompactHeight,
                            onTabChange = { activeTab = it },
                            onSelectEmoji = { emoji, color ->
                                chosenEmoji = emoji
                                if (!isColorCustomized && color != 0L) {
                                    chosenColor = color
                                }
                                viewModel.onSelectEmoji(chosenEmoji, chosenColor)
                            },
                            onSelectColor = { color ->
                                chosenColor = color
                                isColorCustomized = true
                                viewModel.onSelectEmoji(chosenEmoji, chosenColor)
                            },
                            onBack = { currentStep = OnboardingStep.NICKNAME },
                            onStartGame = {
                                if (!isNameValid) {
                                    currentStep = OnboardingStep.NICKNAME
                                    return@StepAvatarContent
                                }
                                viewModel.onSelectEmoji(chosenEmoji, chosenColor)
                                onStartGame(trimmedName, chosenEmoji, chosenColor)
                            }
                        )
                    }
                }
            }
        }

        }
    }
}

private enum class AvatarPickerTab {
    EMOJI,
    COLOR
}

@Composable
private fun ColumnScope.StepNicknameContent(
    playerName: String,
    trimmedName: String,
    isNameValid: Boolean,
    isTooShort: Boolean,
    hasProfanity: Boolean,
    activeColor: Color,
    chosenEmoji: String,
    isTwoPane: Boolean,
    isCompactHeight: Boolean,
    onNameChange: (String) -> Unit,
    onRandomName: () -> Unit,
    onNext: () -> Unit,
    onCancel: () -> Unit
) {
    if (isTwoPane) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .weight(1f),
            horizontalArrangement = Arrangement.spacedBy(20.dp),
            verticalAlignment = Alignment.Top
        ) {
            // Left Column: Step 1 Header & Live Blob Preview
            Column(
                modifier = Modifier
                    .weight(1f)
                    .fillMaxHeight(),
                horizontalAlignment = Alignment.Start,
                verticalArrangement = Arrangement.SpaceBetween
            ) {
                Column(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalAlignment = Alignment.Start
                ) {
                    Text(
                        text = stringResource(Res.string.onboarding_step_1_heading),
                        fontSize = if (isCompactHeight) 18.sp else 22.sp,
                        fontWeight = FontWeight.Black,
                        color = Color.White
                    )
                    Text(
                        text = stringResource(Res.string.onboarding_step_1_subheading),
                        fontSize = if (isCompactHeight) 11.sp else 12.sp,
                        color = Color(0xFF94A3B8)
                    )

                    Spacer(modifier = Modifier.height(if (isCompactHeight) 10.dp else 16.dp))

                    NicknamePreviewCard(
                        activeColor = activeColor,
                        chosenEmoji = chosenEmoji,
                        trimmedName = trimmedName,
                        isCompact = isCompactHeight
                    )
                }
            }

            // Right Column: Nickname Input + Randomizer + Action Buttons
            Column(
                modifier = Modifier
                    .weight(1f)
                    .fillMaxHeight(),
                horizontalAlignment = Alignment.Start,
                verticalArrangement = Arrangement.SpaceBetween
            ) {
                Column(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalAlignment = Alignment.Start
                ) {
                    Text(
                        text = stringResource(Res.string.onboarding_nickname_label),
                        color = Color(0xFF94A3B8),
                        fontSize = 12.sp,
                        fontWeight = FontWeight.SemiBold,
                        modifier = Modifier.fillMaxWidth().padding(bottom = 6.dp)
                    )

                    NicknameInputField(
                        playerName = playerName,
                        isTooShort = isTooShort,
                        isNameValid = isNameValid,
                        hasProfanity = hasProfanity,
                        onNameChange = onNameChange,
                        onRandomName = onRandomName,
                        onNext = onNext
                    )
                }

                NicknameNavButtons(
                    isNameValid = isNameValid,
                    onCancel = onCancel,
                    onNext = onNext,
                    isCompact = isCompactHeight
                )
            }
        }
    } else {
        // Single Column mode (Mobile Portrait)
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .verticalScroll(rememberScrollState()),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Text(
                text = stringResource(Res.string.onboarding_step_1_heading),
                fontSize = if (isCompactHeight) 18.sp else 22.sp,
                fontWeight = FontWeight.Black,
                color = Color.White,
                modifier = Modifier.fillMaxWidth()
            )
            Text(
                text = stringResource(Res.string.onboarding_step_1_subheading),
                fontSize = if (isCompactHeight) 11.sp else 12.sp,
                color = Color(0xFF94A3B8),
                modifier = Modifier.fillMaxWidth()
            )

            Spacer(modifier = Modifier.height(if (isCompactHeight) 10.dp else 16.dp))

            NicknamePreviewCard(
                activeColor = activeColor,
                chosenEmoji = chosenEmoji,
                trimmedName = trimmedName,
                isCompact = isCompactHeight
            )

            Spacer(modifier = Modifier.height(if (isCompactHeight) 10.dp else 16.dp))

            Text(
                text = stringResource(Res.string.onboarding_nickname_label),
                color = Color(0xFF94A3B8),
                fontSize = 12.sp,
                fontWeight = FontWeight.SemiBold,
                modifier = Modifier.fillMaxWidth().padding(bottom = 6.dp)
            )

            NicknameInputField(
                playerName = playerName,
                isTooShort = isTooShort,
                isNameValid = isNameValid,
                hasProfanity = hasProfanity,
                onNameChange = onNameChange,
                onRandomName = onRandomName,
                onNext = onNext
            )

            Spacer(modifier = Modifier.height(if (isCompactHeight) 12.dp else 20.dp))

            NicknameNavButtons(
                isNameValid = isNameValid,
                onCancel = onCancel,
                onNext = onNext,
                isCompact = isCompactHeight
            )
        }
    }
}

@Composable
private fun NicknamePreviewCard(
    activeColor: Color,
    chosenEmoji: String,
    trimmedName: String,
    isCompact: Boolean
) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .background(Color(0xFF0F172A), RoundedCornerShape(16.dp))
            .border(1.dp, Color(0xFF334155), RoundedCornerShape(16.dp))
            .padding(horizontal = if (isCompact) 12.dp else 16.dp, vertical = if (isCompact) 10.dp else 14.dp)
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(if (isCompact) 10.dp else 14.dp)
        ) {
            AvatarBlobPreview(
                emoji = chosenEmoji,
                color = activeColor,
                playerName = trimmedName,
                isCompact = isCompact
            )

            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = trimmedName.ifEmpty { stringResource(Res.string.menu_default_player_name) },
                    fontSize = if (isCompact) 15.sp else 17.sp,
                    fontWeight = FontWeight.Bold,
                    color = Color.White,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
                Spacer(modifier = Modifier.height(2.dp))
                Text(
                    text = stringResource(Res.string.onboarding_live_preview_hint),
                    fontSize = if (isCompact) 10.sp else 11.sp,
                    color = Color(0xFF64748B)
                )
            }
        }
    }
}

@Composable
private fun NicknameInputField(
    playerName: String,
    isTooShort: Boolean,
    isNameValid: Boolean,
    hasProfanity: Boolean,
    onNameChange: (String) -> Unit,
    onRandomName: () -> Unit,
    onNext: () -> Unit
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(8.dp),
        verticalAlignment = Alignment.Top
    ) {
        OutlinedTextField(
            value = playerName,
            onValueChange = { input ->
                if (input.length <= GameConstants.MAX_PLAYER_NAME_LENGTH) {
                    onNameChange(input)
                }
            },
            singleLine = true,
            isError = isTooShort || hasProfanity,
            keyboardOptions = KeyboardOptions(
                imeAction = ImeAction.Next,
                capitalization = KeyboardCapitalization.Words
            ),
            keyboardActions = KeyboardActions(
                onNext = {
                    if (isNameValid) {
                        onNext()
                    }
                }
            ),
            supportingText = {
                when {
                    hasProfanity -> Text(
                        stringResource(Res.string.nickname_not_allowed),
                        color = Color(0xFFFF5252),
                        fontSize = 11.sp
                    )
                    isTooShort -> Text(
                        stringResource(Res.string.onboarding_error_min_chars, GameConstants.MIN_PLAYER_NAME_LENGTH),
                        color = Color(0xFFFF5252),
                        fontSize = 11.sp
                    )
                    playerName.length >= GameConstants.MAX_PLAYER_NAME_LENGTH -> Text(
                        stringResource(Res.string.onboarding_error_max_chars, GameConstants.MAX_PLAYER_NAME_LENGTH),
                        color = Color(0xFFFF9100),
                        fontSize = 11.sp
                    )
                    else -> Text(
                        stringResource(Res.string.onboarding_char_range_hint, GameConstants.MIN_PLAYER_NAME_LENGTH, GameConstants.MAX_PLAYER_NAME_LENGTH),
                        color = Color(0xFF64748B),
                        fontSize = 11.sp
                    )
                }
            },
            placeholder = {
                Text(stringResource(Res.string.onboarding_nickname_placeholder), color = Color(0xFF475569), fontSize = 15.sp)
            },
            textStyle = TextStyle(
                color = Color.White,
                fontSize = 15.sp,
                fontWeight = FontWeight.SemiBold
            ),
            shape = RoundedCornerShape(8.dp),
            colors = OutlinedTextFieldDefaults.colors(
                focusedContainerColor = Color(0xFF0F172A),
                unfocusedContainerColor = Color(0xFF0F172A),
                focusedBorderColor = Color(0xFF00E676),
                unfocusedBorderColor = Color(0xFF334155),
                cursorColor = Color(0xFF00E676),
                errorBorderColor = Color(0xFFFF5252)
            ),
            modifier = Modifier.weight(1f)
        )

        Button(
            onClick = onRandomName,
            shape = RoundedCornerShape(8.dp),
            colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF334155)),
            modifier = Modifier.height(56.dp)
        ) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(4.dp)
            ) {
                Text("🎲", fontSize = 16.sp)
                Text(stringResource(Res.string.onboarding_random_button), fontSize = 12.sp, fontWeight = FontWeight.Bold, color = Color.White)
            }
        }
    }
}

@Composable
private fun NicknameNavButtons(
    isNameValid: Boolean,
    onCancel: () -> Unit,
    onNext: () -> Unit,
    isCompact: Boolean
) {
    val buttonHeight = if (isCompact) 40.dp else 46.dp
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(10.dp)
    ) {
        OutlinedButton(
            onClick = onCancel,
            shape = RoundedCornerShape(12.dp),
            modifier = Modifier.weight(1f).height(buttonHeight)
        ) {
            Text(stringResource(Res.string.onboarding_cancel), color = Color(0xFF94A3B8), fontSize = if (isCompact) 12.sp else 13.sp)
        }

        Button(
            onClick = onNext,
            enabled = isNameValid,
            colors = ButtonDefaults.buttonColors(
                containerColor = Color(0xFF00E5FF),
                disabledContainerColor = Color(0xFF334155)
            ),
            shape = RoundedCornerShape(12.dp),
            modifier = Modifier.weight(1.4f).height(buttonHeight)
        ) {
            Text(
                text = stringResource(Res.string.onboarding_next),
                fontSize = if (isCompact) 12.sp else 13.sp,
                fontWeight = FontWeight.Black,
                color = if (isNameValid) Color.Black else Color(0xFF64748B)
            )
        }
    }
}

@Composable
private fun ColumnScope.StepAvatarContent(
    trimmedName: String,
    chosenEmoji: String,
    chosenColor: Long,
    activeColor: Color,
    activeTab: AvatarPickerTab,
    isSinglePlayer: Boolean,
    isTwoPane: Boolean,
    isCompactHeight: Boolean,
    onTabChange: (AvatarPickerTab) -> Unit,
    onSelectEmoji: (String, Long) -> Unit,
    onSelectColor: (Long) -> Unit,
    onBack: () -> Unit,
    onStartGame: () -> Unit
) {
    val currentSkin = ANIMAL_SKIN_OPTIONS.find { it.emoji == chosenEmoji } ?: ANIMAL_SKIN_OPTIONS.first()

    if (isTwoPane) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .weight(1f),
            horizontalArrangement = Arrangement.spacedBy(if (isCompactHeight) 14.dp else 20.dp),
            verticalAlignment = Alignment.Top
        ) {
            // LEFT PANE: Header, Live Blob Preview, Tab Selector
            Column(
                modifier = Modifier
                    .weight(0.92f)
                    .fillMaxHeight(),
                horizontalAlignment = Alignment.Start
            ) {
                Text(
                    text = stringResource(Res.string.onboarding_step_2_heading),
                    fontSize = if (isCompactHeight) 18.sp else 22.sp,
                    fontWeight = FontWeight.Black,
                    color = Color.White
                )

                Spacer(modifier = Modifier.height(if (isCompactHeight) 8.dp else 12.dp))

                AvatarPreviewCard(
                    activeColor = activeColor,
                    chosenEmoji = chosenEmoji,
                    trimmedName = trimmedName,
                    currentSkin = currentSkin,
                    isCompact = isCompactHeight
                )

                Spacer(modifier = Modifier.height(if (isCompactHeight) 8.dp else 12.dp))

                AvatarTabSelector(
                    activeTab = activeTab,
                    onTabChange = onTabChange
                )
            }

            // RIGHT PANE: Grid of Emoji Skins or Colors + Nav Buttons on Bottom Right
            Column(
                modifier = Modifier
                    .weight(1.08f)
                    .fillMaxHeight(),
                verticalArrangement = Arrangement.SpaceBetween
            ) {
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .weight(1f)
                        .background(Color(0xFF0F172A).copy(alpha = 0.5f), RoundedCornerShape(16.dp))
                        .border(1.dp, Color(0xFF334155), RoundedCornerShape(16.dp))
                        .padding(if (isCompactHeight) 8.dp else 12.dp)
                ) {
                    if (activeTab == AvatarPickerTab.EMOJI) {
                        LazyVerticalGrid(
                            columns = GridCells.Adaptive(minSize = if (isCompactHeight) 68.dp else 78.dp),
                            modifier = Modifier.fillMaxSize(),
                            verticalArrangement = Arrangement.spacedBy(8.dp),
                            horizontalArrangement = Arrangement.spacedBy(8.dp)
                        ) {
                            items(ANIMAL_SKIN_OPTIONS) { skin ->
                                SkinGridItem(
                                    skin = skin,
                                    isSelected = skin.emoji == chosenEmoji,
                                    activeColor = activeColor,
                                    isCompact = isCompactHeight,
                                    onClick = { onSelectEmoji(skin.emoji, skin.color) }
                                )
                            }
                        }
                    } else {
                        LazyVerticalGrid(
                            columns = GridCells.Adaptive(minSize = if (isCompactHeight) 68.dp else 78.dp),
                            modifier = Modifier.fillMaxSize(),
                            verticalArrangement = Arrangement.spacedBy(8.dp),
                            horizontalArrangement = Arrangement.spacedBy(8.dp)
                        ) {
                            items(COLOR_OPTIONS) { colorOpt ->
                                ColorGridItem(
                                    colorOpt = colorOpt,
                                    isSelected = colorOpt.color == chosenColor,
                                    chosenEmoji = chosenEmoji,
                                    isCompact = isCompactHeight,
                                    onClick = { onSelectColor(colorOpt.color) }
                                )
                            }
                        }
                    }
                }

                Spacer(modifier = Modifier.height(if (isCompactHeight) 8.dp else 12.dp))

                AvatarNavButtons(
                    onBack = onBack,
                    onStartGame = onStartGame,
                    isCompact = isCompactHeight,
                    isSinglePlayer = isSinglePlayer
                )
            }
        }
    } else {
        // Single Column mode (Mobile Portrait)
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .weight(1f),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Text(
                text = stringResource(Res.string.onboarding_step_2_heading),
                fontSize = if (isCompactHeight) 18.sp else 22.sp,
                fontWeight = FontWeight.Black,
                color = Color.White,
                modifier = Modifier.fillMaxWidth()
            )

            Spacer(modifier = Modifier.height(if (isCompactHeight) 6.dp else 10.dp))

            AvatarPreviewCard(
                activeColor = activeColor,
                chosenEmoji = chosenEmoji,
                trimmedName = trimmedName,
                currentSkin = currentSkin,
                isCompact = isCompactHeight
            )

            Spacer(modifier = Modifier.height(if (isCompactHeight) 6.dp else 10.dp))

            AvatarTabSelector(
                activeTab = activeTab,
                onTabChange = onTabChange
            )

            Spacer(modifier = Modifier.height(8.dp))

            Box(
                modifier = Modifier
                    .weight(1f)
                    .fillMaxWidth()
            ) {
                if (activeTab == AvatarPickerTab.EMOJI) {
                    LazyVerticalGrid(
                        columns = GridCells.Adaptive(minSize = if (isCompactHeight) 68.dp else 78.dp),
                        modifier = Modifier.fillMaxSize(),
                        verticalArrangement = Arrangement.spacedBy(8.dp),
                        horizontalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        items(ANIMAL_SKIN_OPTIONS) { skin ->
                            SkinGridItem(
                                skin = skin,
                                isSelected = skin.emoji == chosenEmoji,
                                activeColor = activeColor,
                                isCompact = isCompactHeight,
                                onClick = { onSelectEmoji(skin.emoji, skin.color) }
                            )
                        }
                    }
                } else {
                    LazyVerticalGrid(
                        columns = GridCells.Adaptive(minSize = if (isCompactHeight) 68.dp else 78.dp),
                        modifier = Modifier.fillMaxSize(),
                        verticalArrangement = Arrangement.spacedBy(8.dp),
                        horizontalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        items(COLOR_OPTIONS) { colorOpt ->
                            ColorGridItem(
                                colorOpt = colorOpt,
                                isSelected = colorOpt.color == chosenColor,
                                chosenEmoji = chosenEmoji,
                                isCompact = isCompactHeight,
                                onClick = { onSelectColor(colorOpt.color) }
                            )
                        }
                    }
                }
            }

            Spacer(modifier = Modifier.height(10.dp))

            AvatarNavButtons(
                    onBack = onBack,
                    onStartGame = onStartGame,
                    isCompact = isCompactHeight,
                    isSinglePlayer = isSinglePlayer
                )
        }
    }
}

@Composable
private fun AvatarPreviewCard(
    activeColor: Color,
    chosenEmoji: String,
    trimmedName: String,
    currentSkin: AnimalSkinOption,
    isCompact: Boolean
) {
    Card(
        colors = CardDefaults.cardColors(containerColor = Color(0xFF0F172A)),
        shape = RoundedCornerShape(16.dp),
        border = BorderStroke(1.dp, Color(0xFF334155)),
        modifier = Modifier.fillMaxWidth().clip(RoundedCornerShape(16.dp))
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = if (isCompact) 10.dp else 14.dp, vertical = if (isCompact) 8.dp else 10.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(if (isCompact) 10.dp else 14.dp)
        ) {
            AvatarBlobPreview(
                emoji = chosenEmoji,
                color = activeColor,
                playerName = trimmedName,
                isCompact = isCompact
            )

            Column {
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(6.dp)
                ) {
                    Text(
                        text = currentSkin.localizedName(),
                        color = activeColor,
                        fontSize = if (isCompact) 14.sp else 16.sp,
                        fontWeight = FontWeight.Bold
                    )
                    Text(chosenEmoji, fontSize = if (isCompact) 14.sp else 16.sp)
                }
                Spacer(modifier = Modifier.height(2.dp))
                Text(
                    text = stringResource(Res.string.onboarding_playing_as, trimmedName.ifEmpty { stringResource(Res.string.menu_default_player_name) }),
                    fontSize = if (isCompact) 11.sp else 12.sp,
                    fontWeight = FontWeight.SemiBold,
                    color = Color(0xFFCBD5E1),
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            }
        }
    }
}

@Composable
private fun AvatarTabSelector(
    activeTab: AvatarPickerTab,
    onTabChange: (AvatarPickerTab) -> Unit
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .background(Color(0xFF0F172A), RoundedCornerShape(14.dp))
            .padding(4.dp),
        horizontalArrangement = Arrangement.spacedBy(4.dp)
    ) {
        // Choose Avatar Tab
        Box(
            modifier = Modifier
                .weight(1f)
                .clip(RoundedCornerShape(10.dp))
                .background(
                    if (activeTab == AvatarPickerTab.EMOJI) Color(0xFF1E293B) else Color.Transparent
                )
                .border(
                    width = if (activeTab == AvatarPickerTab.EMOJI) 1.dp else 0.dp,
                    color = if (activeTab == AvatarPickerTab.EMOJI) Color(0xFF00E5FF).copy(alpha = 0.6f) else Color.Transparent,
                    shape = RoundedCornerShape(10.dp)
                )
                .clickable { onTabChange(AvatarPickerTab.EMOJI) }
                .padding(vertical = 8.dp),
            contentAlignment = Alignment.Center
        ) {
            Text(
                text = stringResource(Res.string.onboarding_tab_emoji),
                fontSize = 13.sp,
                fontWeight = if (activeTab == AvatarPickerTab.EMOJI) FontWeight.Bold else FontWeight.Medium,
                color = if (activeTab == AvatarPickerTab.EMOJI) Color(0xFF00E5FF) else Color(0xFF94A3B8)
            )
        }

        // Choose Background Color Tab
        Box(
            modifier = Modifier
                .weight(1f)
                .clip(RoundedCornerShape(10.dp))
                .background(
                    if (activeTab == AvatarPickerTab.COLOR) Color(0xFF1E293B) else Color.Transparent
                )
                .border(
                    width = if (activeTab == AvatarPickerTab.COLOR) 1.dp else 0.dp,
                    color = if (activeTab == AvatarPickerTab.COLOR) Color(0xFF00E5FF).copy(alpha = 0.6f) else Color.Transparent,
                    shape = RoundedCornerShape(10.dp)
                )
                .clickable { onTabChange(AvatarPickerTab.COLOR) }
                .padding(vertical = 8.dp),
            contentAlignment = Alignment.Center
        ) {
            Text(
                text = stringResource(Res.string.onboarding_tab_color),
                fontSize = 13.sp,
                fontWeight = if (activeTab == AvatarPickerTab.COLOR) FontWeight.Bold else FontWeight.Medium,
                color = if (activeTab == AvatarPickerTab.COLOR) Color(0xFF00E5FF) else Color(0xFF94A3B8)
            )
        }
    }
}

@Composable
private fun AvatarNavButtons(
    onBack: () -> Unit,
    onStartGame: () -> Unit,
    isCompact: Boolean,
    isSinglePlayer: Boolean
) {
    val buttonHeight = if (isCompact) 40.dp else 46.dp
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(10.dp)
    ) {
        OutlinedButton(
            onClick = onBack,
            shape = RoundedCornerShape(12.dp),
            modifier = Modifier.weight(1f).height(buttonHeight)
        ) {
            Text(stringResource(Res.string.onboarding_back), color = Color(0xFF94A3B8), fontSize = if (isCompact) 12.sp else 13.sp)
        }

        Button(
            onClick = onStartGame,
            colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF00E676)),
            shape = RoundedCornerShape(12.dp),
            modifier = Modifier.weight(1.4f).height(buttonHeight)
        ) {
            Text(
                text = stringResource(
                    if (isSinglePlayer) Res.string.menu_start_single_player
                    else Res.string.onboarding_start_playing
                ),
                fontSize = if (isCompact) 12.sp else 13.sp,
                fontWeight = FontWeight.Black,
                color = Color.Black
            )
        }
    }
}

@Composable
private fun SkinGridItem(
    skin: AnimalSkinOption,
    isSelected: Boolean,
    activeColor: Color,
    isCompact: Boolean,
    onClick: () -> Unit
) {
    val skinThemeColor = Color(skin.color)

    Card(
        onClick = onClick,
        colors = CardDefaults.cardColors(
            containerColor = if (isSelected) skinThemeColor.copy(alpha = 0.15f) else Color(0xFF0F172A)
        ),
        shape = RoundedCornerShape(16.dp),
        border = BorderStroke(
            width = if (isSelected) 2.dp else 1.dp,
            color = if (isSelected) Color(0xFF00E5FF) else Color(0xFF334155)
        ),
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(16.dp))
    ) {
        Column(
            horizontalAlignment = Alignment.CenterHorizontally,
            modifier = Modifier
                .fillMaxWidth()
                .padding(vertical = if (isCompact) 6.dp else 10.dp, horizontal = 4.dp)
        ) {
            Box(
                modifier = Modifier
                    .size(if (isCompact) 50.dp else 60.dp)
                    .clip(CircleShape)
                    .background(
                        if (isSelected) activeColor.copy(alpha = 0.35f) else Color(0xFF1E293B),
                        CircleShape
                    )
                    .border(
                        width = if (isSelected) 2.5.dp else 1.dp,
                        color = if (isSelected) Color(0xFF00E5FF) else Color(0xFF475569),
                        shape = CircleShape
                    ),
                contentAlignment = Alignment.Center
            ) {
                Text(
                    text = skin.emoji,
                    fontSize = if (isCompact) 24.sp else 28.sp,
                    textAlign = TextAlign.Center
                )
            }

            Spacer(modifier = Modifier.height(if (isCompact) 4.dp else 6.dp))

            Text(
                text = skin.localizedName(),
                fontSize = if (isCompact) 10.sp else 11.sp,
                fontWeight = if (isSelected) FontWeight.Bold else FontWeight.Medium,
                color = if (isSelected) Color(0xFF00E5FF) else Color(0xFFCBD5E1),
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
                textAlign = TextAlign.Center
            )
        }
    }
}

@Composable
private fun ColorGridItem(
    colorOpt: ColorOption,
    isSelected: Boolean,
    chosenEmoji: String,
    isCompact: Boolean,
    onClick: () -> Unit
) {
    val optColor = Color(colorOpt.color)

    Card(
        onClick = onClick,
        colors = CardDefaults.cardColors(
            containerColor = if (isSelected) optColor.copy(alpha = 0.2f) else Color(0xFF0F172A)
        ),
        shape = RoundedCornerShape(16.dp),
        border = BorderStroke(
            width = if (isSelected) 2.dp else 1.dp,
            color = if (isSelected) Color(0xFF00E5FF) else Color(0xFF334155)
        ),
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(16.dp))
    ) {
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .padding(vertical = if (isCompact) 6.dp else 8.dp),
            contentAlignment = Alignment.Center
        ) {
            Box(
                modifier = Modifier
                    .size(if (isCompact) 50.dp else 60.dp)
                    .clip(CircleShape)
                    .background(
                        brush = Brush.radialGradient(
                            colors = listOf(optColor, optColor.copy(alpha = 0.85f))
                        ),
                        shape = CircleShape
                    )
                    .border(
                        width = if (isSelected) 2.5.dp else 1.dp,
                        color = if (isSelected) Color.White else Color.White.copy(alpha = 0.35f),
                        shape = CircleShape
                    ),
                contentAlignment = Alignment.Center
            ) {
                Text(
                    text = chosenEmoji,
                    fontSize = if (isCompact) 24.sp else 28.sp,
                    textAlign = TextAlign.Center
                )
            }
        }
    }
}

